class BundleBuilder extends HTMLElement {
  static MOBILE_HEADER = 110;
  constructor() {
    super();
    this.classList.add("loading-init");
    this.confirmBtns = this.querySelectorAll(".bundle-builder__confirm");
    this.addToCartBtn = this.querySelector(".bundle-builder-add-to-cart");
    this.addRow = this.querySelector(".bundle-builder__add-row");
    this.removeIcons = this.querySelectorAll(".icon-remove");
    this.mainRowsContainer = this.querySelector(".bundle-builder__rows");
    this.moreRowsContainer = this.querySelector(".bundle-builder__more-rows");
    this.addRowsIndexEl = this.addRow?.querySelector(".add-row__title-index");
    this.savingsMessage = this.querySelector(
      ".bundle-builder__savings-message"
    );
    this.styleIcons = this.querySelectorAll(".style-icon");
    this.multiTiresBtns = this.querySelectorAll(".multi-tires__btn");
    this.ignorePackImageSwap = false;
    this.isBlackFridayVariety =
      Array.isArray(window?.productData?.tags) &&
      window.productData.tags.includes("black-friday-variety");
    this.isBlackFriday =
      Array.isArray(window?.productData?.tags) &&
      window.productData.tags.includes("black-friday-giftset");

    if (this.isBlackFriday && !this.isBlackFridayVariety) {
      this.applyBlackFridayGiftsetMode();
    }

    if (this.isBlackFridayVariety) {
      this.applyBlackFridayVarietyMode();
    }

    this.currencyPrefix = this.dataset.currencyPrefix || "";
    this.confirmBtns.forEach((btn) => {
      btn.addEventListener("click", this.onConfirmBtnClick.bind(this));
    });
    this.addToCartBtn.addEventListener(
      "click",
      this.onAddToCartBtnClick.bind(this)
    );
    this.addRow?.addEventListener("click", this.onAddRowClick.bind(this));
    this.removeIcons.forEach((icon) => {
      icon.addEventListener("click", this.onRemoveIconClick.bind(this));
    });
    this.styleIcons.forEach((icon) => {
      icon.addEventListener("click", this.onStyleIconClick.bind(this));
    });
    this.multiTiresBtns.forEach((btn) => {
      btn.addEventListener("click", this.onMultiTiresBtnClick.bind(this));
    });
    window.addEventListener("load", () => {
      const params = new URLSearchParams(window.location.search);
      const pack = params.get("pack");
      if (!pack) {
        this.classList.remove("loading");
        return;
      }
      const btn = this.querySelector(`.multi-tires__btn[data-items="${pack}"]`);
      if (btn) btn.click();
      this.classList.remove("loading");
    });
    document.addEventListener(
      "DOMContentLoaded",
      this.singlesAndPacksSavings.bind(this)
    );
    document.addEventListener(
      "DOMContentLoaded",
      this.initialPackPrice.bind(this)
    );
    this.addEventListener("bundle-row-clicked", () => {
      this.ignorePackImageSwap = true;
    });
    this.updateAddRowTitle();

    this.calcSavings();
    /* If Black friday gift set product, open first/only row to be visible */
    if (this.isBlackFriday || this.isBlackFridayVariety) {
      const openVisibleRow = () => {
        const visibleRow = this.querySelector(
          "bundle-builder-row:not(.hidden)"
        );
        if (visibleRow) {
          // Make sure the element is ready
          requestAnimationFrame(() => {
            visibleRow.classList.add("active");
            visibleRow.body.style.maxHeight = getComputedStyle(
              visibleRow.querySelector(".bundle-builder__body-inner")
            ).height;
          });
        } else {
          // If rows not yet in DOM, try again shortly
          setTimeout(openVisibleRow, 100);
        }
      };
      openVisibleRow();
    }

    window.addEventListener("load", () => {
      setTimeout(() => {
        this.checkGiftBoxInventory();
      }, 300);
    });
  }

  static scrollOffset() {
    return 80;
  }

  static scrollRowHeaderIntoView(headerEl, offsetPx = 0, smooth = true) {
    if (!headerEl) return;

    /* ───────── phones: whole document scrolls ───────── */
    if (matchMedia("(max-width: 749px)").matches) {
      const y =
        headerEl.getBoundingClientRect().top + window.scrollY - offsetPx;
      window.scrollTo({ top: y, behavior: smooth ? "smooth" : "auto" });
      return;
    }

    /* ───────── desktops: find inner scroll pane ─────── */
    const root = document.scrollingElement || document.documentElement;
    
    /* Force layout recalculation first to ensure accurate dimensions after dynamic DOM changes */
    void headerEl.offsetHeight;
    
    let container = headerEl.parentElement;


    container = document.querySelector(".product-info__inner");
    /* Force layout recalculation again after container detection to ensure accurate container dimensions */
    void container.offsetHeight;

    const isiOS =
      /iP(ad|hone|od)/.test(navigator.userAgent) && !window.MSStream;
    const wantsSmooth = smooth && !isiOS;

    /* Wait for layout to settle after screen width changes, then recalculate bounding rects */
    requestAnimationFrame(() => {
      // Force layout recalculation after RAF to ensure accurate dimensions after resize
      void container.offsetHeight;
      void headerEl.offsetHeight;
      
      /* target position inside that pane - recalculated after layout settles */
      const cRect = container.getBoundingClientRect();
      const hRect = headerEl.getBoundingClientRect();
      const target = hRect.top - cRect.top + container.scrollTop - offsetPx -10;

      if (isiOS && container !== root)
        container.style.webkitOverflowScrolling = "touch";

      if (isiOS) {
        headerEl.scrollIntoView({ block: "start", behavior: "smooth" });
        if (offsetPx)
          requestAnimationFrame(() =>
            container.scrollBy({ top: -offsetPx, behavior: "auto" })
          );
      } else {
        container.scrollTo({
          top: target,
          behavior: wantsSmooth ? "smooth" : "auto",
        });
      }
    });
  }

  /* Check giftbox inventory */
  async checkGiftBoxInventory() {
    // Only apply on BF products
    const tags = window?.productData?.tags || [];
    const isBF =
      tags.includes("black-friday-giftset") ||
      tags.includes("black-friday-variety") ||
      tags.includes("black-friday-ownpack");

    if (!isBF) return;

    const handle = (window?.productData?.handle || "").toLowerCase();
    let boxHandle = null;

    if (handle.includes("3-pack")) boxHandle = "3-pack-giftbox";
    if (handle.includes("5-pack")) boxHandle = "5-pack-giftbox";

    if (!boxHandle) return;

    try {
      const res = await fetch(`/products/${boxHandle}.js`);
      if (!res.ok) throw new Error("Giftbox JSON not found");

      const product = await res.json();
      const totalInventory = product.variants.reduce(
        (sum, v) => sum + (v.inventory_quantity || 0),
        0
      );

      if (totalInventory <= 0) {
        const btn = this.querySelector(".bundle-builder-add-to-cart");

        // 🛑 Disable ATC button
        if (btn) {
          btn.classList.add("disabled");
          btn.textContent = "Sold out";
        }

        // 🛑 Disable entire bundle-builder interactions
        this.style.pointerEvents = "none";
        this.style.opacity = "0.55";
      }
    } catch (err) {
      console.error("Giftbox inventory check failed:", err);
    }
  }

  onConfirmBtnClick(e) {
    const btn = e.currentTarget;

    /* ---------- 1. locate the <bundle-builder-row> ---------- */
    let thisRow = btn.closest("bundle-builder-row");
    if (!thisRow) {
      const outer = btn.closest(".bundle-builder-row__outer");
      thisRow = outer ? outer.querySelector("bundle-builder-row") : null;
    }
    if (!thisRow) {
      console.warn(
        "Confirm button is not inside a <bundle-builder-row>. " +
          "Check your template or wrappers."
      );
      return;
    }

    /* ---------- 2. house-keeping ---------- */
    const thisStep = btn.closest(".bundle-builder-step");
    const thisRowIndex = Number(thisRow.dataset.index);
    const nextRow =
      thisRow
        .closest(".bundle-builder__rows")
        ?.querySelector(
          `bundle-builder-row[data-index="${thisRowIndex + 1}"]`
        ) || null;

    if (thisStep) {
      const counter = thisStep.querySelector(".selected-counter");
      if (counter && Number(counter.textContent) < thisRowIndex) {
        counter.textContent = thisRowIndex; // bump header counter
      }
    }

    /* ---------- 3. still rows left in this step ---------- */
    if (nextRow) {
      const isMobile = matchMedia("(max-width:749px)").matches;

      /* 3a. collapse current row */
      thisRow.classList.remove("to-select");
      thisRow.header.click();

      /* 3b. prepare & open next row */
      nextRow.classList.remove("disabled");
      nextRow.classList.add("to-select");

      if (isMobile) {
        const body = thisRow.querySelector(".bundle-builder__body");
        const openNext = () => nextRow.header.click();
        body
          ? body.addEventListener("transitionend", openNext, { once: true })
          : openNext(); // no animation → open now
      } else {
        // BundleBuilder.scrollRowHeaderIntoView(nextRow.header, 0, false);
        nextRow.header.click();
      }
      return; // done for this click
    }

    /* ---------- 4. no next row – maybe another step? ---------- */
    if (thisStep) {
      const nextStep = thisStep.nextElementSibling;
      if (nextStep) {
        // close current row & step
        thisRow.classList.remove("to-select");
        thisRow.header.click();

        thisStep.classList.remove("to-select");
        thisStep.header.click();

        // activate next step
        nextStep.classList.remove("disabled");
        nextStep.classList.add("to-select");
        nextStep.header.click();
      } else {
        // last row of last step – enable Add-to-Cart
        this.onLastConfirmBtnClick(e);
      }
    } else {
      // safety net (shouldn’t happen)
      this.onLastConfirmBtnClick(e);
    }
  }

  onLastConfirmBtnClick(e) {
    const thisRow = e.target.closest(".bundle-builder-row");
    const thisStep = e.target.closest(".bundle-builder-step");

    // 1. Close the current row
    thisRow.classList.remove("to-select");
    thisRow.header.click();

    // 2. Close the current step
    if (thisStep) {
      thisStep.classList.remove("to-select");
      thisStep.header.click();
    }

    // 3. Activate add to cart button
    this.addToCartBtn.innerHTML = "Add to cart";
    this.addToCartBtn.classList.remove("disabled");
  }

  onAddToCartBtnClick(e) {
    const btn = e.currentTarget;
    const savingsEl = this.querySelector(
      ".bundle-builder__savings-message .savings"
    );

    let bundleSavings = 0;
    if (savingsEl) {
      bundleSavings = savingsEl.textContent.replace(/[^0-9.]/g, "");
    }

    if (btn.classList.contains("disabled")) {
      this.highlightToSelectRow();
      this.highlightToSelectStep();
      return;
    }

    const items = [];

    // 🟣 Black Friday giftset logic
    if (this.isBlackFriday) {
      // Only use visible rows for Black Friday bundles
      const rowsContainer = this.querySelector(".bundle-builder__rows");
      const rows = [...rowsContainer.querySelectorAll("bundle-builder-row")];

      rows.forEach((row) => {
        const variantInput = row.querySelector(".product-form__variant-id");
        if (variantInput) {
          const variantId = variantInput.value;
          items.push({
            id: variantId,
            quantity: 1,
            properties: {
              _BundleSavings: `$${bundleSavings}`,
            },
          });
        }
      });

      // Add shared bundle info
      const bundleKey = `BF-${Date.now()}`;

      const mainImage = window?.productData?.featured_image
        ? `https:${window.productData.featured_image}`
        : "https://cdn.shopify.com/s/files/1/0000/0000/files/default-placeholder.jpg"; // fallback just in case
      const bundleTitle = window?.productData?.title || "Black Friday Pack";

      items.forEach((item, i) => {
        item.properties["_GiftSet"] = "true";
        item.properties["_BundleKey"] = bundleKey;
        item.properties["_BundleTitle"] = bundleTitle;
        item.properties["_BundleMainImage"] = mainImage;
        item.properties["_BundleItem"] = `Item ${i + 1}`;
      });
      // Detect which box variant to use based on title
      const productTitle = (window?.productData?.title || "").toLowerCase();
      let boxVariantId = "42150281248862"; // default 3-pack

      if (productTitle.includes("5 pack") || productTitle.includes("5-pack")) {
        boxVariantId = "42150281674846"; // use 5-pack box
      }
      // Add Gift Box (only once)
      items.push({
        id: boxVariantId, // BOX variant ID
        quantity: 1,
        properties: {
          _BundleSavings: `$${bundleSavings}`,
          _GiftSet: "true",
          _BundleKey: bundleKey,
          _BundleTitle: bundleTitle,
          _BundleMainImage: mainImage,
          _BundleItem: "Gift Card",
        },
      });
    }
    // BLACK FRIDAY GIFTSET VARIETY
    else if (this.isBlackFridayVariety) {
      const rowsContainer = this.querySelector(".bundle-builder__rows");
      const allRows = [...rowsContainer.querySelectorAll("bundle-builder-row")];
      const items = [];

      // Detect pack count
      let packCount = 3;
      const handle = window?.productData?.handle?.toLowerCase() || "";
      const title = window?.productData?.title?.toLowerCase() || "";
      const activeBtn = this.querySelector(".multi-tires__btn.active");
      if (handle.includes("5-pack") || activeBtn?.dataset.items === "5")
        packCount = 5;

      // Collect first packCount rows (even if hidden)
      allRows.slice(0, packCount).forEach((row) => {
        const variantInput = row.querySelector(".product-form__variant-id");
        if (!variantInput) return;
        const variantId = variantInput.value;
        items.push({
          id: variantId,
          quantity: 1,
          properties: { _BundleSavings: `$${bundleSavings}` },
        });
      });

      // Add shared bundle info
      const bundleKey = `BFV-${Date.now()}`;
      const mainImage = window?.productData?.featured_image
        ? `https:${window.productData.featured_image}`
        : "https://cdn.shopify.com/s/files/1/0000/0000/files/default-placeholder.jpg";
      const bundleTitle = window?.productData?.title || "Variety Pack";

      items.forEach((item, i) => {
        item.properties["_GiftSet"] = "true";
        item.properties["_BundleKey"] = bundleKey;
        item.properties["_BundleTitle"] = bundleTitle;
        item.properties["_BundleMainImage"] = mainImage;
        item.properties["_BundleItem"] = `Item ${i + 1}`;
      });
      // Detect which box variant to use based on title
      const productTitle = (window?.productData?.title || "").toLowerCase();
      let boxVariantId = "42150281248862"; // default 3-pack

      if (productTitle.includes("5 pack") || productTitle.includes("5-pack")) {
        boxVariantId = "42150281674846"; // use 5-pack box
      }
      // Add giftset box (only once)
      items.push({
        id: boxVariantId, // replace with real gift card variant ID
        quantity: 1,
        properties: {
          _GiftSet: "true",
          _BundleKey: bundleKey,
          _BundleTitle: bundleTitle,
          _BundleMainImage: mainImage,
          _BundleItem: "Gift Box",
          _BundleSavings: `$${bundleSavings}`,
        },
      });

      console.log(`🛍️ Variety pack (${packCount}) items:`, items);
      this.bundleBuilderATC(items);
      return;
    }
    // 🟣 BLACK FRIDAY OWN PACK LOGIC
    else if (
      Array.isArray(window?.productData?.tags) &&
      window.productData.tags.includes("black-friday-ownpack")
    ) {
      const rowsContainer = this.querySelector(".bundle-builder__rows");
      const allRows = [...rowsContainer.querySelectorAll("bundle-builder-row")];
      const items = [];

      // Collect all variant IDs normally
      allRows.forEach((row) => {
        const variantInput = row.querySelector(".product-form__variant-id");
        if (!variantInput) return;
        const variantId = variantInput.value;
        if (!variantId) return;
        items.push({
          id: variantId,
          quantity: 1,
          properties: { _BundleSavings: `$${bundleSavings}` },
        });
      });

      // Shared bundle data
      const bundleKey = `BFO-${Date.now()}`;
      const mainImage =
        window.productData.featured_image ||
        (window.productData.images?.length
          ? window.productData.images[0]
          : null);

      const bundleTitle = window?.productData?.title || "Own Black Friday Pack";

      // Add group info to each selected item
      items.forEach((item, i) => {
        item.properties["_GiftSet"] = "true";
        item.properties["_BundleKey"] = bundleKey;
        item.properties["_BundleTitle"] = bundleTitle;
        item.properties["_BundleMainImage"] = mainImage;
        item.properties["_BundleItem"] = `Item ${i + 1}`;
      });
      // Detect which box variant to use based on title
      const productTitle = (window?.productData?.title || "").toLowerCase();
      let boxVariantId = "42150281248862"; // default 3-pack

      if (productTitle.includes("5 pack") || productTitle.includes("5-pack")) {
        boxVariantId = "42150281674846"; // use 5-pack box
      }
      // Add the gift box
      if (!window._giftCardAddedOnce) {
        items.push({
          id: boxVariantId,
          quantity: 1,
          properties: {
            _GiftSet: "true",
            _BundleKey: bundleKey,
            _BundleTitle: bundleTitle,
            _BundleMainImage: mainImage,
            _BundleItem: "Gift Card",
            _BundleSavings: `$${bundleSavings}`,
          },
        });
        window._giftCardAddedOnce = true;
      }

      console.log(`🛍️ Black Friday Own Pack items:`, items);
      this.bundleBuilderATC(items);
      return;
    }
    // 🟢 Normal bundle logic (everything else)
    else {
      const forms = this.querySelectorAll("form#product-form");
      const variantItemList = {};

      forms.forEach((form) => {
        const isDisabled = form
          .closest(".bundle-builder-row")
          .classList.contains("disabled");
        if (isDisabled) return;

        const styleForm = form.closest(".style-form");
        if (styleForm && styleForm.classList.contains("hidden")) return;

        const variantId = form.querySelector(".product-form__variant-id").value;

        if (variantItemList[variantId]) {
          variantItemList[variantId]++;
        } else {
          variantItemList[variantId] = 1;
        }
      });

      Object.keys(variantItemList).forEach((variantId) => {
      const props = {};

      const finalSaleInput = this.querySelector(
        `.last-chance__line-item-property input.variant--${variantId}`
      );

      if (finalSaleInput && finalSaleInput.value) {
        props["_LPROP"] = finalSaleInput.value;
      }

      items.push({
        id: variantId,
        quantity: variantItemList[variantId],
        properties: props,
      });
    });

    }

    // ✅ Send to cart
    console.log("🛍️ Final items:", items);
    this.bundleBuilderATC(items);
  }

  highlightToSelectRow() {
    const toSelectRow = document.querySelector(".bundle-builder-row.to-select");
    toSelectRow.classList.add("highlight-to-select");
    setTimeout(() => toSelectRow.classList.remove("highlight-to-select"), 1000);
  }

  highlightToSelectStep() {
    const toSelectStep = document.querySelector(
      ".bundle-builder-step.to-select"
    );
    if (!toSelectStep) return;

    toSelectStep.classList.add("highlight-to-select");
    setTimeout(
      () => toSelectStep.classList.remove("highlight-to-select"),
      1000
    );
  }

  formatWithPrefix(amount) {
    const money = Shopify.formatMoney(amount).replace(/(\.|,)00\b/, "");
    return this.currencyPrefix ? this.currencyPrefix + money : money;
  }
  // Used for gift set with 3 black T-shirts
  applyBlackFridayGiftsetMode() {
    const rows = this.querySelectorAll("bundle-builder-row");
    if (!rows.length) return;

    // Update title + variant text for Black Friday
    rows.forEach((row, i) => {
      const titleInner = row.querySelector(".bundle-builder-row__title-inner");
      const variantEl = row.querySelector(
        ".bundle-builder-row__selected-variant"
      );
      const titleIndex = row.querySelector(".bundle-builder-row__title-index");
      const caret = row.querySelector(".icon-caret");

      if (titleInner) {
        titleInner.textContent = "Gift-set";
        if (titleIndex) titleIndex.textContent = ""; // clear index
      }

      if (variantEl) {
        variantEl.textContent = "Select a size";
      }
    });

    // Hide Next button
    this.confirmBtns.forEach((btn) => {
      btn.style.display = "none";
    });
    // 1. Show only the first row visually
    rows.forEach((row, i) => {
      if (i === 0) {
        row.classList.remove("hidden");
        row.style.display = "";
      } else {
        row.classList.add("hidden");
        row.style.display = "none";
      }
    });

    // 2. Find all size radios in the first row
    const firstRow = rows[0];

    const sizeRadios = firstRow.querySelectorAll(
      '.product-form__input input[type="radio"][name*="Size"], .product-form__input input[type="radio"][data-option-name*="Size"]'
    );

    if (!sizeRadios.length) return;
    // Uncheck all radios initially
    sizeRadios.forEach((radio) => {
      radio.checked = false;
      radio.removeAttribute("checked"); // also remove attribute if it's hardcoded in HTML
    });
    sizeRadios.forEach((radio) => {
      radio.addEventListener("change", (e) => {
        const selectedValue = e.target.value;

        // 3. Sync selected size to all hidden rows
        rows.forEach((r, idx) => {
          if (idx === 0) return;
          const hiddenRadio = r.querySelector(
            `input[type="radio"][value="${selectedValue}"]`
          );
          if (hiddenRadio) {
            hiddenRadio.checked = true;
            hiddenRadio.dispatchEvent(new Event("change", { bubbles: true }));
          }
        });

        // 4. Automatically click all confirm buttons (simulate user confirmation)
        this.confirmBtns.forEach((btn) => {
          // Only click buttons in hidden rows (first row stays as is)
          const parentRow = btn.closest("bundle-builder-row");
          if (parentRow && parentRow !== firstRow) {
            btn.click();
          }
        });

        // 5. Enable Add to Cart button after everything syncs
        this.addToCartBtn.classList.remove("disabled");
        this.addToCartBtn.innerHTML = "Add to cart";
      });
    });
  }

  buildVariantAvailabilityFromRows() {
    const rows = this.querySelectorAll(".product-form--bundle-builder");
    if (!rows.length || !window.giftSetColors) return {};

    const colors = window.giftSetColors.split(",").map((c) => c.trim());
    const availabilityMap = {};

    rows.forEach((row) => {
      const scriptEl = row.querySelector("variant-radios script");
      if (!scriptEl) return;

      try {
        const variants = JSON.parse(scriptEl.textContent);
        variants.forEach((v) => {
          const color = (v.option1 || "").trim();
          const size = (v.option2 || "").trim();
          const available = v.available;

          // Only track colors from metafield
          if (!colors.includes(color)) return;
          if (!availabilityMap[color]) availabilityMap[color] = {};
          availabilityMap[color][size] = available;
        });
      } catch (err) {
        console.warn(`Failed to parse variants for row`, err);
      }
    });

    console.log("🗺️ Built variant availability map:", availabilityMap);
    window.variantAvailability = availabilityMap;
    return availabilityMap;
  }
  // Used for Black Friday Variety modes
  applyBlackFridayVarietyMode() {
    const rows = Array.from(this.querySelectorAll("bundle-builder-row"));
    if (!rows.length) return;

    rows.forEach((row) => {
      const titleInner = row.querySelector(".bundle-builder-row__title-inner");
      const variantEl = row.querySelector(
        ".bundle-builder-row__selected-variant"
      );
      const titleIndex = row.querySelector(".bundle-builder-row__title-index");
      if (titleInner) titleInner.textContent = "Gift-set";
      if (titleIndex) titleIndex.textContent = "";
      if (variantEl) variantEl.textContent = "Select a size";
    });

    async function buildAvailabilityFromMainProduct() {
      const currentHandle = (window.productData?.handle || "").toLowerCase();
      let handle;

      if (currentHandle.includes("women") || currentHandle.includes("ladies")) {
        handle = "womens-merino-crew-neck-tee";
      } else {
        handle = "merino-wool-crew-neck-t-shirt";
      }
      const map = {};
      try {
        const res = await fetch(`/products/${handle}.js`);
        if (!res.ok) throw new Error("Failed to load product JSON");
        const data = await res.json();
        data.variants.forEach((v) => {
          const color = (v.option1 || "").trim();
          const size = (v.option2 || "").trim();
          if (!color || !size) return;
          if (!map[color]) map[color] = {};
          map[color][size] = v.available;
        });
        console.log("✅ Variant availability map built:", map);
        window.variantAvailability = map;
        return map;
      } catch (err) {
        console.error("❌ Error building availability map:", err);
        return {};
      }
    }

    requestAnimationFrame(() => {
      buildAvailabilityFromMainProduct().then((map) => {
        console.log("🟢 Live map ready:", map);

        let packCount = 3;
        const title = window?.productData?.title?.toLowerCase() || "";
        const handle = window?.productData?.handle?.toLowerCase() || "";
        const activeBtn = this.querySelector(".multi-tires__btn.active");
        if (
          handle.includes("mens-5-pack-gift-set-variety") ||
          handle.includes("women-s-5-pack-gift-set-variety") ||
          activeBtn?.dataset.items === "5"
        )
          packCount = 5;
        this.confirmBtns.forEach((btn) => (btn.style.display = "none"));
        rows.forEach((row) => {
          row.classList.add("hidden", "disabled");
          row.style.display = "none";
        });
        const firstRow = rows[0];
        if (firstRow) {
          firstRow.classList.remove("hidden", "disabled");
          firstRow.style.display = "";
        }

        let colorOrder = [];
        if (window.giftSetColors)
          colorOrder = window.giftSetColors.split(",").map((c) => c.trim());

        rows.forEach((row, i) => {
          const desiredColor = colorOrder[i];
          if (!desiredColor) return;
          const colorInputs = row.querySelectorAll('input[option="Color"]');
          if (!colorInputs.length) return;
          colorInputs.forEach((inp) => (inp.checked = false));
          const match = Array.from(colorInputs).find(
            (inp) =>
              inp.value.trim().toLowerCase() ===
              desiredColor.trim().toLowerCase()
          );
          if (!match) return;
          match.checked = true;
          match.dispatchEvent(new Event("change", { bubbles: true }));
          const label = row.querySelector(".option-heading__current-color");
          if (label) label.textContent = desiredColor;
          const variantIdInput = row.querySelector(".product-form__variant-id");
          const vr = row.querySelector("variant-radios");
          if (variantIdInput && vr?.dataset?.variants) {
            try {
              const variants = JSON.parse(vr.dataset.variants || "[]");
              const v = variants.find(
                (v) =>
                  (v.option1 || "").trim().toLowerCase() ===
                  desiredColor.trim().toLowerCase()
              );
              if (v) variantIdInput.value = v.id;
            } catch (_) {}
          }
        });

        const combinedColors = colorOrder.filter(Boolean).join(" / ");
        const firstVariantLabel = this.querySelector(
          ".bundle-builder-row__selected-variant"
        );
        if (firstVariantLabel && combinedColors)
          firstVariantLabel.textContent = combinedColors;

        const sizeRadios = firstRow.querySelectorAll(
          '.product-form__input input[type="radio"][option="Size"]'
        );
        if (!sizeRadios.length) return;

        const atcBtn = this.querySelector(".bundle-builder-add-to-cart");
        if (!atcBtn) return;

        // ✅ Fixed size syncing across all rows
        sizeRadios.forEach((radio) => {
          radio.addEventListener("change", (e) => {
            const selectedSize = e.target.value;

            // 🔄 Sync this same size across all rows
            rows.forEach((row, idx) => {
              if (idx === 0) return;
              const sameSize = row.querySelector(
                `input[type="radio"][option="Size"][value="${selectedSize}"]`
              );
              if (sameSize) {
                sameSize.checked = true;
                sameSize.dispatchEvent(new Event("change", { bubbles: true }));
              }
            });

            // 🧠 Availability check
            let allAvailable = true;
            for (const color of colorOrder) {
              const isAvailable = map?.[color]?.[selectedSize];
              if (!isAvailable) {
                allAvailable = false;
                break;
              }
            }

            if (!allAvailable) {
              atcBtn.classList.add("disabled");
              atcBtn.textContent = "Sold out";
            } else {
              atcBtn.classList.remove("disabled");
              atcBtn.textContent = "Add to cart";
            }
          });
        });

        const checkedSize = firstRow.querySelector(
          'input[option="Size"]:checked'
        );
        if (checkedSize) {
          const selectedSize = checkedSize.value;
          let allAvailable = true;
          for (const color of colorOrder) {
            const isAvailable = map?.[color]?.[selectedSize];
            if (!isAvailable) {
              allAvailable = false;
              break;
            }
          }
          if (!allAvailable) {
            atcBtn.classList.add("disabled");
            atcBtn.textContent = "Sold out";
          } else {
            atcBtn.classList.remove("disabled");
            atcBtn.textContent = "Add to cart";
          }
        }

        atcBtn.addEventListener("click", (e) => {
          if (atcBtn.classList.contains("disabled")) {
            e.preventDefault();
            return;
          }
          atcBtn.classList.add("loading");
          setTimeout(() => atcBtn.classList.remove("loading"), 700);
        });

        document.addEventListener("cart:updated", () => {
          atcBtn.classList.remove("disabled", "loading");
          atcBtn.textContent = "Add to cart";
        });
      });
    });
  }

  updateAddRowTitle() {
    if (!this.addRow || !this.savingsMessage) return;

    const titleEl = this.addRow.querySelector(".add-row__title");
    if (!titleEl) return;

    const indexSpan = titleEl.querySelector(".add-row__title-index");
    if (!indexSpan) return;

    const full = Number(this.savingsMessage.dataset.productPriceOriginal);
    const activeBtn = this.querySelector(".multi-tires__btn.active");
    let discount = Number(this.savingsMessage.dataset.productPriceDiscount);
    if (activeBtn) discount = Number(activeBtn.dataset.pricePerItem);

    const extra = full - discount;
    const formatted = Shopify.formatMoney(extra).replace(/(\.|,)00\b/, "");

    let noteSpan = titleEl.querySelector(".extra-savings-note");

    if (extra <= 0) {
      if (noteSpan) noteSpan.remove();
      return;
    }

    if (!noteSpan) {
      noteSpan = document.createElement("span");
      noteSpan.className = "extra-savings-note";
      indexSpan.after(noteSpan);
    }

    noteSpan.textContent = ` – save an extra ${formatted}!`;
  }

  async bundleBuilderATC(items) {
    const cart = document.querySelector("cart-drawer");
    const cartCounterIcon = document.querySelector(
      ".header__icon--cart-wrapper"
    );

    // Add loading animation
    this.addToCartBtn.classList.add("loading");
    cartCounterIcon.classList.add("loading");

    try {
      if (!cart) throw new Error("Cart drawer element not found");

      const formData = {
        sections: cart.getSectionsToRender().map((section) => section.id),
        sections_url: window.location.pathname,
        items,
      };

      cart.setActiveElement(document.activeElement);

      // 🛒 Add bundle items
      const res = await fetch(`${window.Shopify.routes.root}cart/add.js`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(formData),
      });

      const response = await res.json();

      // 🕒 Wait 2.5 seconds to ensure all items finish processing (tweak as needed)
      console.log("⏳ Waiting 2500ms before rendering cart...");
      await new Promise((r) => setTimeout(r, 2500));

      // 🧩 Ensure response has sections before render
      if (response?.sections) {
        cart.renderContents(response);
        console.log("✅ Cart contents rendered successfully.");
      } else {
        console.warn("⚠️ No sections found in response — retrying fetch...");
        // 🌀 Re-fetch the cart state and then render
        const state = await (
          await fetch("/?sections=cart-drawer,cart-icon-bubble")
        ).json();
        cart.renderContents({ sections: state });
      }

      // ✅ Open cart drawer
      cart.openCloseCart();
    } catch (error) {
      console.error("❌ bundleBuilderATC failed:", error);
    } finally {
      // Remove loading states
      this.addToCartBtn.classList.remove("loading");
      cartCounterIcon.classList.remove("loading");

      if (cart?.classList.contains("is-empty")) {
        cart.classList.remove("is-empty");
      }
    }
  }

  onAddRowClick() {
    const addRowsIndexNumber = parseInt(this.addRowsIndexEl.textContent);
    const toAddRowOuter = this.moreRowsContainer.firstElementChild;
    if (!toAddRowOuter) return;
    const toAddRow = toAddRowOuter.querySelector(".bundle-builder-row");
    const toSelectRow = document.querySelector(".bundle-builder-row.to-select");

    // 1. If there's no to-select row, make this new row to-select and disable ATC button
    if (!toSelectRow) {
      toAddRow.classList.remove("disabled");
      toAddRow.classList.add("to-select");
      this.addToCartBtn.classList.add("disabled");
    }

    // 2. Move the new row to the main rows container
    this.mainRowsContainer.insertAdjacentElement("beforeend", toAddRowOuter);

    // 3. Show/hide remove icon
    this.showHideRemoveIcons();

    // 4. Display the new row
    toAddRowOuter.classList.remove("hidden");

    // 5. Increment the add row index
    this.addRowsIndexEl.textContent = addRowsIndexNumber + 1;

    // 6. Hide add row element if there are no more rows
    if (!this.moreRowsContainer.firstElementChild)
      this.addRow.classList.add("hidden");

    // 7. Update multi-tires buttons
    this.updateMultiTiresBtns();

    // 8. Re-calc the savings
    this.calcSavings();

    // 9. Update pack price
    this.updatePackPrice();

    // 10. Show discount amount
    this.updateAddRowTitle();
  }

  showHideRemoveIcons() {
    const addedRows = this.mainRowsContainer.querySelectorAll(
      ".bundle-builder-row__outer"
    );
    if (addedRows.length === 0) return;
    addedRows.forEach((row) => row.classList.remove("show-remove-icon"));
    addedRows[addedRows.length - 1].classList.add("show-remove-icon");
  }

  onRemoveIconClick(e) {
    const addRowsIndexNumber = parseInt(this.addRowsIndexEl.textContent);
    const toRemoveRowOuter = e.target.closest(".bundle-builder-row__outer");
    const toRemoveRow = toRemoveRowOuter.querySelector(".bundle-builder-row");
    const selectedVariantEl = toRemoveRow.querySelector(
      ".bundle-builder-row__selected-variant"
    );

    // 1. Move the row from the main rows container to the more rows container
    this.moreRowsContainer.insertAdjacentElement(
      "afterbegin",
      toRemoveRowOuter
    );

    // 2. Hide and disable it
    toRemoveRowOuter.classList.add("hidden");
    toRemoveRow.classList.remove("to-select");
    toRemoveRow.classList.add("disabled");
    selectedVariantEl.textContent = "";

    // 3. Update add row index and show it
    this.addRowsIndexEl.textContent = addRowsIndexNumber - 1;
    this.addRow.classList.remove("hidden");

    // 4. Show/hide remove icon
    this.showHideRemoveIcons();

    // 5. Check to-select row
    const toSelectRow = document.querySelector(".bundle-builder-row.to-select");
    if (!toSelectRow) {
      this.addToCartBtn.innerHTML = "Add to cart";
      this.addToCartBtn.classList.remove("disabled");
    }

    // 7. Update multi-tires buttons
    this.updateMultiTiresBtns();

    // 8. Re-calc the savings
    this.calcSavings();

    // 9. Update pack price
    this.updatePackPrice();

    // 10. Show discount amount
    this.updateAddRowTitle();
  }

  calcSavings() {
    const numberOfItems = this.mainRowsContainer.childElementCount;

    const originalUnitCents = Number(
      this.savingsMessage?.dataset?.productPriceOriginal
    );
    let discountUnitCents = Number(
      this.savingsMessage?.dataset?.productPriceDiscount
    );

    this.multiTiresBtns.forEach((btn) => {
      if (btn.classList.contains("active")) {
        discountUnitCents = Number(btn.dataset.pricePerItem);
      }
    });

    let totalCents = discountUnitCents * numberOfItems;
    let savingsCents = originalUnitCents * numberOfItems - totalCents;

    const totalEl = this.savingsMessage?.querySelector(".total");
    const savingsEl = this.savingsMessage?.querySelector(".savings");

    if (this.savingsMessage &&this.savingsMessage?.dataset?.basePrice) this.savingsMessage.dataset.basePrice = this.formatWithPrefix(totalCents);
    if (savingsEl)
      this.savingsMessage.dataset.bundleBaseSaveAmount =
        this.formatWithPrefix(savingsCents);

    document.querySelectorAll("bundle-builder-row").forEach((row) => {
      if (
        row.dataset.lastChanceSavings &&
        row.dataset.lastChanceSavings != "0"
      ) {
        savingsCents += parseFloat(row.dataset.lastChanceSavings) * 100;
        totalCents -= parseFloat(row.dataset.lastChanceSavings) * 100;
      }
    });

    if (totalEl)  totalEl.textContent = this.formatWithPrefix(totalCents);
    if (savingsEl) savingsEl.textContent = this.formatWithPrefix(savingsCents);
  }

  onStyleIconClick(e) {
    const styleIcon = e.target.closest(".style-icon");
    const thisRow = styleIcon.closest(".bundle-builder-row");
    const allForms = thisRow.querySelectorAll(".style-form");
    const relatedForm = thisRow.querySelector(styleIcon.dataset.form);
    const variantRadiosEl = relatedForm.querySelector("variant-radios");

    // stopImmediatePropagation
    const input = styleIcon.querySelector('input[type="radio"]');
    input.addEventListener("click", function (e) {
      e.stopImmediatePropagation();
    });

    // 1. Show related form
    allForms.forEach((form) => form.classList.add("hidden"));
    relatedForm.classList.remove("hidden");

    // 2. Upate info
    variantRadiosEl.getSelectedOptions();
    variantRadiosEl.getVariantJSON();
    variantRadiosEl.getSelectedVariant();
    variantRadiosEl.updateProductInfo();
    variantRadiosEl.updateBundleBuilder();
  }

  onMultiTiresBtnClick(e) {
    const btn = e.currentTarget;
    const requiredNumberOfRows = parseInt(btn.dataset.items);
    const currentNumberOfRows = this.mainRowsContainer.childElementCount;
    const multiTiresCount = this.multiTiresBtns.length; // total multi-tires buttons

    //change the page title with the new count
    document.title = document.title.replace(/\d+/, requiredNumberOfRows);

    // Check if a 5-pack button exists anywhere on the page
    const hasFivePackBtn = Array.from(this.multiTiresBtns).some(
      (el) => el.dataset.items === "5"
    );

    this.multiTiresBtns.forEach((el) => el.classList.remove("active"));
    btn.classList.add("active");

    if (requiredNumberOfRows > currentNumberOfRows) {
      const difference = requiredNumberOfRows - currentNumberOfRows;
      for (let index = 0; index < difference; index++) {
        this.addRow.click();
      }
    } else if (requiredNumberOfRows < currentNumberOfRows) {
      const difference = currentNumberOfRows - requiredNumberOfRows;
      for (let index = 0; index < difference; index++) {
        const removeIconsInTheMainRowsContainer =
          this.mainRowsContainer.querySelectorAll(".icon-remove");
        const lastRemoveIcon =
          removeIconsInTheMainRowsContainer[
            removeIconsInTheMainRowsContainer.length - 1
          ];
        lastRemoveIcon.click();
      }
    }

    this.swapPictureSources(btn.dataset.image);

    this.updateAddRowTitle();
  }

  swapPictureSources(imageUrl) {
    // 1) find the very first slide in the slider
    const slide = document.querySelector(
      ".product-media__gallery--slider .keen-slider__slide"
    );
    if (!slide) return;

    // 2) inside that slide, find the <picture> block
    const picture = slide.querySelector("picture");
    if (!picture) return;

    // 3) swap every <source> srcset to the new URL
    picture.querySelectorAll("source").forEach((src) => {
      // keep the " 1x" or " 2x" suffix if you want; here we just use 1x
      src.srcset = imageUrl + " 1x";
    });

    // 4) swap the <img> itself
    const img = picture.querySelector("img.image-cover");
    if (!img) return;
    img.src = imageUrl;
    img.srcset = "";
    img.setAttribute(
      "data-zoom",
      imageUrl.replace(/(\.\w+)(\?.*)?$/, "_2000x$1$2")
    );
  }

  updateMultiTiresBtns() {
    if (this.multiTiresBtns.length === 0) return;
    const currentNumberOfRows = this.mainRowsContainer.childElementCount;

    this.multiTiresBtns.forEach((btn) => {
      const btnItems = parseInt(btn.dataset.items);

      if (currentNumberOfRows >= btnItems) {
        this.multiTiresBtns.forEach((el) => el.classList.remove("active"));
        btn.classList.add("active");
      }
    });
  }

  singlesAndPacksSavings() {
    const holderEl = document.querySelector(
      ".singles-and-packs__savings-holder"
    );
    if (!holderEl) return;
    const savingsEls = document.querySelectorAll(".singles-and-packs__savings");

    holderEl.textContent = this.savingsMessage.dataset.singlesAndPacksSavings;
    savingsEls.forEach((el) => (el.textContent = holderEl.textContent));
  }

  toCents(priceText) {
    const txt = priceText.trim();
    if (/[.,]\d{2}\b/.test(txt)) {
      return Number(txt.replace(/[^\d]/g, ""));
    }
    return Number(txt.replace(/[^\d]/g, "")) * 100;
  }

  initialPackPrice() {
    if (this.classList.contains("bundle-builder--pack")) {
      const packPrice = document.querySelectorAll(".pack-price");
      if (!packPrice.length > 0) return;
      const initialPackPrice = this.savingsMessage.querySelector(".total");

      const first = this.formatWithPrefix(
        this.toCents(this.savingsMessage.querySelector(".total").textContent)
      );
      packPrice.forEach((price) => (price.textContent = first));
      // if current product is bundle -> show pack price
      if (document.querySelector(".singles-and-packs") === null) {
        const singlePrice = document.querySelectorAll(".product-price");
        packPrice.forEach((price) => price.classList.remove("hidden"));
        singlePrice.forEach((price) => price.classList.add("hidden"));
      }
    } else {
      let savingLabel = this.querySelector(".bundle-builder__savings-message");
      if (!savingLabel) return;
      let basePrice = document.querySelector(
        ".product-info-above-description-desktop .price-item--regular"
      ).innerText;
      let savings = document
        .querySelector(
          ".product-info-above-description-desktop .product-price__saving-label"
        )
        .innerText.replace(/[^0-9.]/g, "");
      let currency = basePrice.replace(/[0-9.]/g, "");
      savingLabel.querySelector(".total").innerText = basePrice;
      savingLabel.querySelector(".savings").innerText = currency + savings;
    }
  }

  updatePackPrice() {
    const singlePrice = document.querySelectorAll(".product-price");
    const packPrice = document.querySelectorAll(".pack-price");
    const currentPackPrice = this.savingsMessage.querySelector(".total");
    const savingsLabel = document.querySelectorAll(
      ".product-price__saving-label"
    );
    const currentSavings = this.savingsMessage.querySelector(".savings");

    // Helper function to remove trailing .00 or ,00 if present
    function formatPrice(priceText) {
      priceText = priceText.trim();
      return priceText.replace(/(\.|,)00\b/, "");
    }

    // 1. Pack price - update text content with formatted price
    if (currentPackPrice) {
      const cents = this.toCents(currentPackPrice.textContent);
      const formattedPrice = this.formatWithPrefix(cents);
      packPrice.forEach((el) => (el.textContent = formattedPrice));
      // 2. Update afterpay price
      document.querySelector(".afterpay-price").innerHTML =
        this.formatWithPrefix(String((cents / 4).toFixed(0)));
    }

    singlePrice.forEach((price) => price.classList.add("hidden"));
    packPrice.forEach((price) => price.classList.remove("hidden"));

    // 3. Savings label
    if (savingsLabel.length > 0) {
      savingsLabel.forEach((label) => {
        if (currentSavings) {
          const formattedSavings = formatPrice(currentSavings.textContent);
          label.textContent = `Save ${formattedSavings}`;
        } else {
          label.classList.add("hidden");
        }
      });
    }
  }
}

customElements.define("bundle-builder", BundleBuilder);

class BundleBuilderRow extends HTMLElement {
  constructor() {
    super();
    this.header = this.querySelector(".bundle-builder-row__header");
    this.body = this.querySelector(".bundle-builder__body");

    this.header.addEventListener("click", this.onHeaderClick.bind(this));
    window.addEventListener("resize", this.heightAnimation.bind(this));
  }
  onHeaderClick() {
    // 1) If this row is disabled, flash and bail
    if (this.classList.contains("disabled")) {
      this.highlightToSelectRow();
      return;
    }

    // 2) Collapse every other row
    const collapsingRows = [];
    if (!this.isBlackFriday && !this.isBlackFridayVariety) {
      document.querySelectorAll("bundle-builder-row").forEach((row) => {
        if (row !== this && row.classList.contains("active")) {
          collapsingRows.push(row);
          row.classList.remove("active");
          row.body.style.maxHeight = "0";
        }
      });
    }

    // 3) Toggle this one open
    const opening = !this.classList.contains("active");
    if (opening) {
      this.classList.add("active");
      this.updateRowInfo();
      this.heightAnimation();

      const offset = matchMedia("(max-width:749px)").matches
        ? BundleBuilder.MOBILE_HEADER
        : 0;

      const parentBuilder = this.closest("bundle-builder");
      const isBF = parentBuilder?.isBlackFriday || parentBuilder?.isBlackFridayVariety;

      if (!isBF) {
        // Wait for collapse animations to complete before scrolling
        if (collapsingRows.length > 0) {
          let completedCount = 0;
          const scrollAfterAnimation = () => {
            completedCount++;
            if (completedCount === collapsingRows.length) {
              setTimeout(() => {
                BundleBuilder.scrollRowHeaderIntoView(this.header, offset);
              }, 300);
            }
          };

          collapsingRows.forEach((row) => {
            if (row.body) {
              row.body.addEventListener("transitionend", scrollAfterAnimation, { once: true });
            }
          });
        } else {
          // No rows collapsing, scroll immediately
          setTimeout(() => {
            BundleBuilder.scrollRowHeaderIntoView(this.header, offset);
          }, 300);
        }
      }
    } else {
      // 4) closing
      setTimeout(() => {
        this.classList.remove("active");
        this.body.style.maxHeight = "0";
      }, 450);
    }

    // 5) bubble up so bundle-builder knows we clicked
    this.dispatchEvent(
      new CustomEvent("bundle-row-clicked", { bubbles: true })
    );
  }

  heightAnimation() {
    const innerH = getComputedStyle(
      this.querySelector(".bundle-builder__body-inner")
    ).height;

    if (this.classList.contains("active")) {
      this.body.style.maxHeight = innerH;
    } else {
      this.body.style.maxHeight = "0";
    }
  }

  highlightToSelectRow() {
    if (this.isBlackFriday && !this.isBlackFridayVariety) {
      const toSelectRow = document.querySelector(
        ".bundle-builder-row.to-select"
      );
      toSelectRow.classList.add("highlight-to-select");
      setTimeout(
        () => toSelectRow.classList.remove("highlight-to-select"),
        1000
      );
    }
  }

  updateRowInfo() {
    const styleForms = this.querySelector(".product-style__style-forms");
    let variantRadiosEl;

    if (styleForms) {
      variantRadiosEl = this.querySelector(
        ".style-form:not(.hidden)"
      ).querySelector("variant-radios");
    } else {
      variantRadiosEl = this.querySelector("variant-radios");
    }

    variantRadiosEl.getSelectedOptions();
    variantRadiosEl.getVariantJSON();
    variantRadiosEl.getSelectedVariant();
    variantRadiosEl.updateProductInfo();
    variantRadiosEl.updateBundleBuilder();
  }
}

customElements.define("bundle-builder-row", BundleBuilderRow);

class BundelBuilderStep extends HTMLElement {
  constructor() {
    super();
    this.header = this.querySelector(".bundle-builder-step__header");
    this.body = this.querySelector(".bundle-builder-step__body");

    this.header.addEventListener("click", this.onHeaderClick.bind(this));
  }

  onHeaderClick() {
    if (this.classList.contains("disabled")) {
      this.highlightToSelectStep();
    } else {
      const allSteps = document.querySelectorAll(".bundle-builder-step");

      // 1. Show/hide current step
      if (this.classList.contains("active")) {
        this.classList.remove("active");
      } else {
        allSteps.forEach((el) => el.classList.remove("active"));
        this.classList.add("active");
      }
    }
  }

  highlightToSelectStep() {
    const toSelectStep = document.querySelector(
      ".bundle-builder-step.to-select"
    );
    if (!toSelectStep) return;

    toSelectStep.classList.add("highlight-to-select");
    setTimeout(
      () => toSelectStep.classList.remove("highlight-to-select"),
      1000
    );
  }
}

customElements.define("bundle-builder-step", BundelBuilderStep);
